import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Music, ImagePlus, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { UPLOAD_GENRES, SUBTAGS } from "@/lib/hubs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Header } from "@/pages/dashboard/Insights";

export const MAX = 200 * 1024 * 1024;
export const EXT = ["mp3", "wav", "flac", "m4a", "ogg", "aac"];

export async function analyze(file) {
  if (file.size > 80 * 1024 * 1024) return { peaks: [], duration: 0 };
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  try {
    const buf = await ctx.decodeAudioData(await file.arrayBuffer());
    const ch = buf.getChannelData(0);
    const n = 140, step = Math.floor(ch.length / n);
    const peaks = [];
    for (let i = 0; i < n; i++) {
      let m = 0;
      for (let j = i * step; j < (i + 1) * step; j += 64) m = Math.max(m, Math.abs(ch[j] || 0));
      peaks.push(m);
    }
    const top = Math.max(...peaks, 0.01);
    return { peaks: peaks.map((p) => +(p / top).toFixed(3)), duration: buf.duration };
  } catch (e) { return { peaks: [], duration: 0 }; } finally { ctx.close(); }
}

const EMPTY = { title: "", genres: [], subtag: "", women_hub: false, privacy: "public", description: "", credits: "", copyright: false };

export const Uploader = ({ embedded = false, onUploaded }) => {
  const nav = useNavigate();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ peaks: [], duration: 0 });
  const [cover, setCover] = useState(null);
  const [drag, setDrag] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [pct, setPct] = useState(null);
  const [result, setResult] = useState(null);
  const pick = async (fl) => {
    if (!fl) return;
    const ext = fl.name.split(".").pop().toLowerCase();
    if (!EXT.includes(ext)) return toast.error("Unsupported file type");
    if (fl.size > MAX) return toast.error("File exceeds 200MB");
    setFile(fl);
    setF((s) => ({ ...s, title: s.title || fl.name.replace(/\.[^.]+$/, "") }));
    const m = await analyze(fl);
    setMeta(m);
    if (m.duration && m.duration <= 10) toast.error("Track must be longer than 10 seconds");
  };
  const toggleGenre = (g) => setF((s) => ({ ...s, genres: s.genres.includes(g) ? s.genres.filter((x) => x !== g) : s.genres.length >= 2 ? s.genres : [...s.genres, g] }));
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", f.title);
    fd.append("genres", f.genres.join(","));
    fd.append("subtag", f.subtag);
    fd.append("women_hub", f.women_hub);
    fd.append("privacy", f.privacy);
    fd.append("description", f.description);
    fd.append("credits", f.credits);
    fd.append("copyright_confirmed", f.copyright);
    fd.append("peaks", JSON.stringify(meta.peaks));
    fd.append("client_duration", meta.duration || 0);
    if (cover) fd.append("cover", cover);
    setPct(0);
    try {
      const { data } = await api.post("/tracks/upload", fd, { onUploadProgress: (e) => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success("Track published");
      if (onUploaded) {
        onUploaded(data);
        setFile(null); setCover(null); setF(EMPTY); setMeta({ peaks: [], duration: 0 });
      } else setResult(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setPct(null); }
  };
  if (result) return (
    <div data-testid="upload-result" className="surface p-8 max-w-2xl reveal">
      <h2 className="font-display text-3xl font-black">“{result.title}” is live.</h2>
      {result.tag_flag?.status === "flagged" ? (
        <div data-testid="upload-tag-flag" className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200 flex gap-3"><AlertTriangle size={18} className="shrink-0" /><div>Our audio assistant noticed a possible tag inconsistency: {result.tag_flag.issues.join("; ")}. Your track stays published. You can request a manual verification in Track manager.</div></div>
      ) : <p className="text-zinc-400 mt-2">Tags verified. No issues detected.</p>}
      <div className="flex gap-2 mt-6">
        <button data-testid="upload-view-track" onClick={() => nav(`/track/${result.track_id}`)} className="h-11 px-5 rounded-full bg-white text-black font-semibold">View track</button>
        <button data-testid="upload-another" onClick={() => { setResult(null); setFile(null); setCover(null); setF(EMPTY); }} className="h-11 px-5 rounded-full border border-white/15">Upload another</button>
      </div>
    </div>
  );
  const valid = file && f.title && f.genres.length >= 1 && f.copyright && pct === null;
  return (
    <div data-testid="track-uploader">
      {!embedded && <Header eyebrow="Track manager" title="Upload a track" />}
      <div
        data-testid="upload-dropzone" onClick={() => input.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
        className={`cursor-pointer rounded-3xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-[var(--hub)] bg-[var(--hub)]/10" : "border-white/15 hover:border-white/30"}`}>
        <input ref={input} type="file" accept={EXT.map((e) => "." + e).join(",")} hidden data-testid="upload-file-input" onChange={(e) => pick(e.target.files[0])} />
        {file ? <div className="flex items-center justify-center gap-3"><Music className="text-[var(--hub)]" /><span className="font-semibold">{file.name}</span><span className="font-mono text-xs text-zinc-500">{(file.size / 1048576).toFixed(1)}MB{meta.duration ? ` · ${Math.round(meta.duration)}s` : ""}</span></div>
          : <><UploadCloud className="mx-auto text-[var(--hub)]" size={36} /><div className="font-display text-xl font-bold mt-3">Drag & drop your audio</div><div className="text-sm text-zinc-500 mt-1">MP3, WAV, FLAC, M4A, OGG · max 200MB · min 10 seconds</div></>}
      </div>
      <div className={`grid gap-6 mt-6 ${embedded ? "" : "lg:grid-cols-[1fr_240px]"}`}>
        <div className="space-y-5">
          <Input data-testid="upload-title-input" placeholder="Track title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 bg-white/5 border-white/10" />
          <div>
            <div className="eyebrow mb-2">Primary genres · max 2 ({f.genres.length}/2)</div>
            <div className="flex flex-wrap gap-2">{UPLOAD_GENRES.map((h) => {
              const on = f.genres.includes(h.key), full = f.genres.length >= 2 && !on;
              return <button key={h.key} type="button" data-testid={`upload-genre-${h.key}`} disabled={full} onClick={() => toggleGenre(h.key)} className={`px-3 h-9 rounded-full text-sm border transition-colors disabled:opacity-30 ${on ? "text-black border-transparent" : "border-white/10 hover:border-white/30"}`} style={on ? { background: h.color } : {}}>{h.name}</button>;
            })}</div>
          </div>
          <div>
            <div className="eyebrow mb-2">Sub-genre / vocal profile · max 1</div>
            <div className="flex flex-wrap gap-2">{SUBTAGS.map((s) => <button key={s} type="button" data-testid={`upload-subtag-${s.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => setF({ ...f, subtag: f.subtag === s ? "" : s })} className={`px-3 h-8 rounded-full text-xs border ${f.subtag === s ? "bg-white text-black border-white" : "border-white/10 hover:border-white/30"}`}>{s}</button>)}</div>
          </div>
          <Textarea data-testid="upload-description-input" placeholder="Description, lyrics…" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-white/5 border-white/10" rows={3} />
          <Input data-testid="upload-credits-input" placeholder="Credits (producer, writers, mix & master…)" value={f.credits} onChange={(e) => setF({ ...f, credits: e.target.value })} maxLength={1000} className="h-11 bg-white/5 border-white/10" />
          <div className="flex gap-2">{["public", "private"].map((p) => <button key={p} data-testid={`upload-privacy-${p}`} onClick={() => setF({ ...f, privacy: p })} className={`px-4 h-9 rounded-full text-sm border capitalize ${f.privacy === p ? "bg-white text-black" : "border-white/10"}`}>{p}</button>)}</div>
          <label className="flex gap-3 items-start text-sm text-zinc-300 p-4 rounded-xl border border-white/10">
            <Checkbox data-testid="upload-women-checkbox" checked={f.women_hub} onCheckedChange={(v) => setF({ ...f, women_hub: !!v })} className="mt-0.5" />
            <span><b>Optional · 100% Women in Music showcase.</b> I self-declare as a female independent creator/producer on this track. This editorial channel is optional and does not affect distribution or monetization.</span>
          </label>
          <label className="flex gap-3 items-start text-sm text-zinc-300 p-4 rounded-xl border border-[var(--hub)]/40 bg-[var(--hub)]/5">
            <Checkbox data-testid="upload-copyright-checkbox" checked={f.copyright} onCheckedChange={(v) => setF({ ...f, copyright: !!v })} className="mt-0.5" />
            <span><b>Required.</b> I confirm I hold 100% of the master and publishing rights to this recording (or all necessary licences), and I accept the Terms of Service.</span>
          </label>
        </div>
        <div>
          <label data-testid="upload-cover-dropzone" className={`block aspect-square rounded-2xl border border-dashed border-white/15 overflow-hidden cursor-pointer hover:border-white/30 ${embedded ? "max-w-[200px]" : ""}`}>
            <input type="file" accept="image/png,image/jpeg,image/webp" hidden data-testid="upload-cover-input" onChange={(e) => setCover(e.target.files[0])} />
            {cover ? <img src={URL.createObjectURL(cover)} alt="" className="w-full h-full object-cover" /> : <div className="h-full flex flex-col items-center justify-center text-zinc-500 text-sm gap-2"><ImagePlus /> Cover art</div>}
          </label>
        </div>
      </div>
      {pct !== null && <div className="mt-6"><Progress value={pct} className="h-2 bg-white/10" /><div className="text-xs font-mono text-zinc-500 mt-1">{pct < 100 ? `Uploading ${pct}%` : "Processing & running tag analysis…"}</div></div>}
      <button data-testid="upload-submit-button" disabled={!valid} onClick={submit} className="mt-6 h-12 px-8 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-40 inline-flex items-center gap-2">{pct !== null && <Loader2 className="animate-spin" size={16} />} Publish track</button>
    </div>
  );
};
